import { getApprovalUserValue, getPersonDisplayName } from './personNameI18n';
import { DEPARTMENT_NAME_OPTIONS, getLocalizedField, localizedLabel } from './localizedField';

/**
 * 조직도(/department/treeUser) 노드와 그 노드로 만든 결재선 항목의 다국어 표시용 헬퍼.
 * 직원 노드: type 'user'(또는 userId 있음), 이름 번역(nameEngFirst/Last, nameVie, nameChn), 직위 rankData
 * 부서 노드: type 'depart', 부서 번역(nameEng, nameVie, nameChn)
 */

export const isTreeUserNode = (node) => node?.type === 'user' || (!!node?.userId && node?.type !== 'depart');

export const getTreeUserName = (node) => getPersonDisplayName(node, undefined, node?.title || node?.name);

export const getTreeUserRank = (t, node) =>
	node?.userRank ? localizedLabel(t, { ...node.rankData, name: node.userRank }) : '';

export const getTreeUserPosition = (t, node) =>
	node?.userPosition ? localizedLabel(t, { ...node.userPositionData, name: node.userPosition }) : '';

export const getTreeDeptName = (node) => getLocalizedField(node) || node?.title || '';

/** userId → { node, depts } (같은 직원이 여러 부서에 있을 수 있어 부서는 목록으로 보관) */
export const buildTreeUserMap = (nodes, map = new Map(), parent = null) => {
	for (const node of nodes || []) {
		const isUser = isTreeUserNode(node);
		if (isUser) {
			const entry = map.get(node.userId) || { node, depts: [] };
			if (parent) entry.depts.push(parent);
			map.set(node.userId, entry);
		}
		buildTreeUserMap(node.children, map, isUser ? parent : node);
	}
	return map;
};

/** 검색어 비교용 문자열: 원본 + 번역 이름·직위·부서 */
export const getTreeNodeSearchTexts = (t, node) => {
	if (!node) return [];
	if (isTreeUserNode(node)) {
		const { nameEngFirst, nameEngLast, nameVie, nameChn, rankData } = node;
		return [
			node.title, node.name, getTreeUserName(node), nameEngFirst, nameEngLast, nameVie, nameChn,
			[nameEngLast, nameEngFirst].filter(Boolean).join(' '), [nameEngFirst, nameEngLast].filter(Boolean).join(' '),
			node.userRank, getTreeUserRank(t, node), rankData?.nameEng, rankData?.nameVie, rankData?.nameChn,
			node.userDepartment, node.companyNumber, node.email,
		];
	}
	return [node.title, node.name, node.nameEng, node.nameVie, node.nameChn];
};

export const matchesTreeSearch = (t, node, keyword) => {
	const text = String(keyword || '').toLowerCase();
	if (!text) return true;
	return getTreeNodeSearchTexts(t, node).some((v) => v && String(v).toLowerCase().includes(text));
};

/**
 * 결재선·참조자 항목 표시: 저장된 번역본(userEng 등) → 조직도 대응표 → 원본.
 * holder = 결재선 항목(block) 또는 참조자, person = 사용자 객체(block.user 또는 참조자 자신)
 */
export const getLineUserName = (holder, person, treeUserMap) => {
	const entry = treeUserMap?.get(person?.userId);
	return getApprovalUserValue(holder, 'name', { fallback: entry ? getTreeUserName(entry.node) : getPersonDisplayName(person) });
};

export const getLineUserRank = (t, holder, person, treeUserMap) => {
	const entry = treeUserMap?.get(person?.userId);
	const fallback = entry && entry.node.userRank === person?.rank ? getTreeUserRank(t, entry.node) : person?.rank ? t(person.rank) : '';
	return getApprovalUserValue(holder, 'rank', { fallback });
};

export const getLineUserDept = (holder, person, treeUserMap) => {
	const dept = treeUserMap?.get(person?.userId)?.depts.find((d) => d.name === person?.department);
	return getApprovalUserValue(holder, 'department', { fallback: dept ? getTreeDeptName(dept) : person?.department || '' });
};

/**
 * 조직도에서 고른 결재자 항목(user)에 붙일 부서 번역 필드.
 * 서버 번역본(userEng 등)이 아직 없는 새 항목도 요청 화면에서 부서를 번역해 보여주기 위함
 * @returns {object} { departmentNameEng, departmentNameVie, departmentNameChn } 중 값이 있는 것
 */
export const getTreeUserDeptLangs = (userId, departmentName, treeUserMap) => {
	const dept = treeUserMap?.get(userId)?.depts.find((d) => d.name === departmentName);
	if (!dept) return {};
	return Object.fromEntries(
		[['departmentNameEng', dept.nameEng], ['departmentNameVie', dept.nameVie], ['departmentNameChn', dept.nameChn]]
			.filter(([, value]) => value),
	);
};

/** 결재선 사용자(withApprovalLineLangs 결과)의 부서 표시명: 서버 번역본 → 조직도 번역 → 원본 */
export const getApprovalLineDept = (person) =>
	getApprovalUserValue(person, 'department', {
		fallback: getLocalizedField(person, 'department', undefined, DEPARTMENT_NAME_OPTIONS) || '',
	});
